import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { AbsencePeriod, Announcement, AttendanceMark, ChoirEvent, EventDocument, Fee, Member, Role, Room, Rsvp, Settings, TransportEntry } from '../types';
import { members as initialMembers } from '../data/members';
import { events as initialEvents, pretekleOdsotnosti, zacetneSobe, zacetniPrevoz, TODAY } from '../data/events';
import { announcements as initialAnnouncements } from '../data/announcements';
import { fees as initialFees } from '../data/fees';
import { defaultSettings } from '../data/settings';
import { formatDate, parseDate } from '../utils/labels';
interface Toast {
  id: number;
  besedilo: string;
}
interface AppState {
  today: string;
  role: Role | null;
  currentMemberId: string | null;
  currentMember: Member | null;
  /** Izrecno dovoljenje „Dostop do osebnih podatkov“. */
  canViewPrivate: boolean;
  members: Member[];
  events: ChoirEvent[];
  announcements: Announcement[];
  fees: Fee[];
  settings: Settings;
  rsvps: Record<string, Rsvp>;
  absences: AbsencePeriod[];
  attendance: Record<string, Record<string, AttendanceMark>>;
  rooms: Record<string, Record<string, string>>;
  transport: Record<string, Record<string, TransportEntry>>;
  toasts: Toast[];
  login: (role: Role, memberId: string) => void;
  logout: () => void;
  setRsvp: (eventId: string, rsvp: Rsvp, memberId?: string) => void;
  getRsvp: (eventId: string, memberId?: string) => Rsvp | undefined;
  addAbsence: (a: Omit<AbsencePeriod, 'id'>) => void;
  markRead: (announcementId: string, memberId?: string) => void;
  saveEvent: (e: ChoirEvent) => void;
  addAnnouncement: (a: Announcement) => void;
  saveAttendance: (eventId: string, marks: Record<string, AttendanceMark>) => void;
  setFeePaid: (memberId: string, leto: number, placano: boolean) => void;
  updateSettings: (s: Settings) => void;
  updateMember: (m: Member) => void;
  addDocument: (eventId: string, doc: Omit<EventDocument, 'id'>) => void;
  removeDocument: (eventId: string, docId: string) => void;
  sendMinutes: (eventId: string, prejemniki: string[], besedilo: string) => void;
  addRoom: (eventId: string, room: Omit<Room, 'id'>) => void;
  removeRoom: (eventId: string, roomId: string) => void;
  assignRoom: (eventId: string, memberId: string, roomId: string | null) => void;
  setTransport: (eventId: string, memberId: string, entry: TransportEntry | null) => void;
  assignPassenger: (eventId: string, memberId: string, driverId: string | null) => void;
  expectedFor: (e: ChoirEvent) => Member[];
  isExpected: (e: ChoirEvent, memberId: string) => boolean;
  visibleEvents: (memberId: string) => ChoirEvent[];
  upcoming: ChoirEvent[];
  past: ChoirEvent[];
  pushToast: (besedilo: string) => void;
  dismissToast: (id: number) => void;
}
const Ctx = createContext<AppState | null>(null);
export function AppProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [role, setRole] = useState<Role | null>(null);
  const [currentMemberId, setCurrentMemberId] = useState<string | null>(null);
  const [members, setMembers] = useState<Member[]>(initialMembers);
  const [events, setEvents] = useState<ChoirEvent[]>(initialEvents);
  const [announcements, setAnnouncements] = useState<Announcement[]>(initialAnnouncements);
  const [fees, setFees] = useState<Fee[]>(initialFees);
  const [settings, setSettings] = useState<Settings>(defaultSettings);
  const [rooms, setRooms] = useState<Record<string, Record<string, string>>>(zacetneSobe);
  const [transport, setTransportState] = useState<Record<string, Record<string, TransportEntry>>>(zacetniPrevoz);
  const [rsvps, setRsvps] = useState<Record<string, Rsvp>>({
    'm04:e11': {
      status: 'ne_morem',
      razlog: 'Službene obveznosti'
    },
    'm06:e11': {
      status: 'ne_morem',
      razlog: 'Bolezen'
    },
    'm14:e11': {
      status: 'ne_morem',
      razlog: 'Družinske obveznosti'
    },
    'm23:e11': {
      status: 'pridem'
    },
    'm11:e11': {
      status: 'pridem'
    },
    'm03:e23': {
      status: 'pridem'
    },
    'm04:e23': {
      status: 'pridem'
    },
    'm05:e23': {
      status: 'pridem'
    }
  });
  const [absences, setAbsences] = useState<AbsencePeriod[]>([{
    id: 'ab1',
    memberId: 'm30',
    od: '2026-11-16',
    do: '2026-12-06',
    razlog: 'Bolezen',
    opomba: 'Operacija in okrevanje.'
  }]);
  const [attendance, setAttendance] = useState<Record<string, Record<string, AttendanceMark>>>(() => {
    const base: Record<string, Record<string, AttendanceMark>> = {};
    Object.entries(pretekleOdsotnosti).forEach(([eventId, absent]) => {
      const marks: Record<string, AttendanceMark> = {};
      initialMembers.forEach(m => {
        if (m.type === 'nekdanji' || m.type === 'neaktivni') return;
        marks[m.id] = absent.includes(m.id) ? 'odsoten' : 'prisoten';
      });
      base[eventId] = marks;
    });
    return base;
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const pushToast = useCallback((besedilo: string) => {
    const id = Date.now() + Math.random();
    setToasts(t => [...t, {
      id,
      besedilo
    }]);
    window.setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3600);
  }, []);
  const dismissToast = useCallback((id: number) => {
    setToasts(t => t.filter(x => x.id !== id));
  }, []);
  const currentMember = useMemo(() => members.find(m => m.id === currentMemberId) ?? null, [members, currentMemberId]);
  const canViewPrivate = role === 'vodstvo' && !!currentMemberId && settings.dostopOsebnihPodatkov.includes(currentMemberId);
  const login = useCallback((r: Role, memberId: string) => {
    setRole(r);
    setCurrentMemberId(memberId);
  }, []);
  const logout = useCallback(() => {
    setRole(null);
    setCurrentMemberId(null);
  }, []);
  const setRsvp = useCallback((eventId: string, rsvp: Rsvp, memberId?: string) => {
    const id = memberId ?? currentMemberId;
    if (!id) return;
    setRsvps(r => ({
      ...r,
      [`${id}:${eventId}`]: rsvp
    }));
  }, [currentMemberId]);
  const getRsvp = useCallback((eventId: string, memberId?: string) => {
    const id = memberId ?? currentMemberId;
    if (!id) return undefined;
    return rsvps[`${id}:${eventId}`];
  }, [rsvps, currentMemberId]);
  const addAbsence = useCallback((a: Omit<AbsencePeriod, 'id'>) => {
    setAbsences(list => [...list, {
      ...a,
      id: `ab${list.length + 1}`
    }]);
  }, []);
  const markRead = useCallback((announcementId: string, memberId?: string) => {
    const id = memberId ?? currentMemberId;
    if (!id) return;
    setAnnouncements(list => list.map(a => a.id === announcementId && !a.prebrali.includes(id) ? {
      ...a,
      prebrali: [...a.prebrali, id]
    } : a));
  }, [currentMemberId]);
  const saveEvent = useCallback((e: ChoirEvent) => {
    setEvents(list => {
      const exists = list.some(x => x.id === e.id);
      const next = exists ? list.map(x => x.id === e.id ? e : x) : [...list, e];
      return next.sort((a, b) => a.datum.localeCompare(b.datum));
    });
  }, []);
  const addAnnouncement = useCallback((a: Announcement) => {
    setAnnouncements(list => [a, ...list]);
  }, []);
  const saveAttendance = useCallback((eventId: string, marks: Record<string, AttendanceMark>) => {
    setAttendance(a => ({
      ...a,
      [eventId]: marks
    }));
  }, []);
  const setFeePaid = useCallback((memberId: string, leto: number, placano: boolean) => {
    setFees(list => list.map(f => f.memberId === memberId && f.leto === leto ? {
      ...f,
      placano,
      datumPlacila: placano ? TODAY : undefined
    } : f));
  }, []);
  const updateSettings = useCallback((s: Settings) => setSettings(s), []);
  const updateMember = useCallback((m: Member) => {
    setMembers(list => list.map(x => x.id === m.id ? m : x));
  }, []);
  const addDocument = useCallback((eventId: string, doc: Omit<EventDocument, 'id'>) => {
    setEvents(list => list.map(e => e.id === eventId ? {
      ...e,
      dokumenti: [...(e.dokumenti ?? []), {
        ...doc,
        id: `d${Math.random().toString(36).slice(2, 7)}`
      }]
    } : e));
  }, []);
  const removeDocument = useCallback((eventId: string, docId: string) => {
    setEvents(list => list.map(e => e.id === eventId ? {
      ...e,
      dokumenti: (e.dokumenti ?? []).filter(d => d.id !== docId)
    } : e));
  }, []);
  const sendMinutes = useCallback((eventId: string, prejemniki: string[], besedilo: string) => {
    const event = events.find(e => e.id === eventId);
    if (!event) return;
    const zapisnik = (event.dokumenti ?? []).find(d => d.kind === 'zapisnik');
    setAnnouncements(list => [{
      id: `a${Math.random().toString(36).slice(2, 7)}`,
      naslov: `Zapisnik: ${event.naslov ?? 'dogodek'}`,
      besedilo,
      prioriteta: 'pomembno',
      avtor: currentMember ? `${currentMember.ime} ${currentMember.priimek}` : 'Vodstvo zbora',
      objavljeno: new Date().toISOString(),
      obcinstvoLabel: 'Udeleženci dogodka',
      prejemniki,
      zahtevaPotrditev: false,
      prebrali: [],
      priloga: zapisnik ? {
        ime: zapisnik.ime,
        link: zapisnik.link,
        eventId
      } : undefined
    }, ...list]);
    setEvents(list => list.map(e => e.id === eventId ? {
      ...e,
      zapisnikPoslan: TODAY
    } : e));
  }, [events, currentMember]);
  const addRoom = useCallback((eventId: string, room: Omit<Room, 'id'>) => {
    setEvents(list => list.map(e => {
      if (e.id !== eventId || !e.potovanje?.nastanitev) return e;
      return {
        ...e,
        potovanje: {
          ...e.potovanje,
          nastanitev: {
            ...e.potovanje.nastanitev,
            sobe: [...e.potovanje.nastanitev.sobe, {
              ...room,
              id: `r${Math.random().toString(36).slice(2, 6)}`
            }]
          }
        }
      };
    }));
  }, []);
  const removeRoom = useCallback((eventId: string, roomId: string) => {
    setEvents(list => list.map(e => {
      if (e.id !== eventId || !e.potovanje?.nastanitev) return e;
      return {
        ...e,
        potovanje: {
          ...e.potovanje,
          nastanitev: {
            ...e.potovanje.nastanitev,
            sobe: e.potovanje.nastanitev.sobe.filter(r => r.id !== roomId)
          }
        }
      };
    }));
    setRooms(r => {
      const zaDogodek = {
        ...(r[eventId] ?? {})
      };
      Object.keys(zaDogodek).forEach(memberId => {
        if (zaDogodek[memberId] === roomId) delete zaDogodek[memberId];
      });
      return {
        ...r,
        [eventId]: zaDogodek
      };
    });
  }, []);
  const assignRoom = useCallback((eventId: string, memberId: string, roomId: string | null) => {
    setRooms(r => {
      const zaDogodek = {
        ...(r[eventId] ?? {})
      };
      if (roomId) zaDogodek[memberId] = roomId;else delete zaDogodek[memberId];
      return {
        ...r,
        [eventId]: zaDogodek
      };
    });
  }, []);
  const setTransport = useCallback((eventId: string, memberId: string, entry: TransportEntry | null) => {
    setTransportState(t => {
      const zaDogodek = {
        ...(t[eventId] ?? {})
      };
      if (entry) zaDogodek[memberId] = entry;else delete zaDogodek[memberId];
      return {
        ...t,
        [eventId]: zaDogodek
      };
    });
  }, []);
  const assignPassenger = useCallback((eventId: string, memberId: string, driverId: string | null) => {
    setTransportState(t => {
      const zaDogodek = {
        ...(t[eventId] ?? {})
      };
      const obstojec = zaDogodek[memberId] ?? {
        nacin: 'potrebujem' as const
      };
      zaDogodek[memberId] = {
        ...obstojec,
        voznikId: driverId ?? undefined
      };
      return {
        ...t,
        [eventId]: zaDogodek
      };
    });
  }, []);

  /** Ali dogodek velja za člana — funkcije so neodvisne od tipa članstva. */
  const matches = useCallback((e: ChoirEvent, m: Member): boolean => {
    if (m.type === 'nekdanji') return false;
    switch (e.obcinstvo) {
      case 'vsi':
        return m.type !== 'neaktivni';
      case 'aktivni':
        return m.type === 'aktivni';
      case 'glasovi':
        return !!e.glasovi?.includes(m.voice) && m.type !== 'neaktivni';
      case 'funkcije':
        return !!e.funkcije?.some(f => m.functions.includes(f));
      case 'izbrani':
        return !!e.clani?.includes(m.id);
      case 'sestavljeno':
        return !!e.tipi?.includes(m.type) || !!e.glasovi?.includes(m.voice) || !!e.funkcije?.some(f => m.functions.includes(f)) || !!e.clani?.includes(m.id);
      default:
        return false;
    }
  }, []);
  const expectedFor = useCallback((e: ChoirEvent): Member[] => members.filter(m => matches(e, m)), [members, matches]);
  const isExpected = useCallback((e: ChoirEvent, memberId: string) => {
    const m = members.find(x => x.id === memberId);
    return !!m && matches(e, m);
  }, [members, matches]);
  const visibleEvents = useCallback((memberId: string) => {
    const m = members.find(x => x.id === memberId);
    if (!m) return [];
    return events.filter(e => matches(e, m) || e.obcinstvo === 'aktivni' && m.type === 'gostujoci');
  }, [events, members, matches]);
  const upcoming = useMemo(() => events.filter(e => parseDate(e.datumDo ?? e.datum).getTime() >= parseDate(TODAY).getTime()).sort((a, b) => a.datum.localeCompare(b.datum)), [events]);
  const past = useMemo(() => events.filter(e => parseDate(e.datumDo ?? e.datum).getTime() < parseDate(TODAY).getTime()).sort((a, b) => b.datum.localeCompare(a.datum)), [events]);
  const value: AppState = {
    today: TODAY,
    role,
    currentMemberId,
    currentMember,
    canViewPrivate,
    members,
    events,
    announcements,
    fees,
    settings,
    rsvps,
    absences,
    attendance,
    rooms,
    transport,
    toasts,
    login,
    logout,
    setRsvp,
    getRsvp,
    addAbsence,
    markRead,
    saveEvent,
    addAnnouncement,
    saveAttendance,
    setFeePaid,
    updateSettings,
    updateMember,
    addDocument,
    removeDocument,
    sendMinutes,
    addRoom,
    removeRoom,
    assignRoom,
    setTransport,
    assignPassenger,
    expectedFor,
    isExpected,
    visibleEvents,
    upcoming,
    past,
    pushToast,
    dismissToast
  };
  return <Ctx.Provider value={value} data-id="element-463" data-mp="2nanjmz4tx92i">{children}</Ctx.Provider>;
}
export function useApp(): AppState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useApp mora biti uporabljen znotraj AppProvider');
  return ctx;
}

/** Pomožno: lep zapis datuma pošiljanja zapisnika. */
export function zapisnikStatus(datum?: string): string | null {
  return datum ? `Zapisnik poslan ${formatDate(datum)}` : null;
}